import React, { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { fetchMeasuresForPotByDate, fetchIrrigationsForPot } from '../../../api/api_pots';
import { fetchMeasuresForHubByDate } from '../../../api/api_hubs';
import moment from 'moment';
import loadingGif from '../../assets/loading.gif';

const PotDetailsADMIN = () => {
    const { translations } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const { pot } = location.state;
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return date;
    });
    const [endDate, setEndDate] = useState(new Date());
    const [selectedTimeRange, setSelectedTimeRange] = useState();

    const [irrigations, setIrrigations] = useState([]);
    const [chartData, setChartData] = useState({
        temperature: [],
        minerals: [],
        soilMoisture: [],
        soilMoisture_2: [],
        airHumidity: [],
        illumination: [],
    });
    const [rawPotData, setRawPotData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const timeRangeOptions = [
        translations.one_week, 
        translations.two_weeks, 
        translations.one_month, 
        translations.three_months, 
        translations.six_months, 
        translations.one_year
    ];

    const [visibleTemperatureLines, setVisibleTemperatureLines] = useState({ value: true, smaValue: true });
    const [visibleMineralsLines, setVisibleMineralsLines] = useState({ value: true, smaValue: true });
    const [visibleSoilMoistureLines, setVisibleSoilMoistureLines] = useState({ realMain: true, realAux: true, procMain: true, procAux: true });
    const [visibleAirHumidityLines, setVisibleAirHumidityLines] = useState({ value: true, smaValue: true });
    const [visibleIlluminationLines, setVisibleIlluminationLines] = useState({ value: true, smaValue: true });

    const handleLegendClickTemperature = (e) => {
        const { dataKey } = e;
        setVisibleTemperatureLines(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };
    const handleLegendClickMinerals = (e) => {
        const { dataKey } = e;
        setVisibleMineralsLines(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };
    const handleLegendClickSoilMoisture = (e) => {
        const { dataKey } = e;
        setVisibleSoilMoistureLines(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };
    const handleLegendClickAirHumidity = (e) => {
        const { dataKey } = e;
        setVisibleAirHumidityLines(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };
    const handleLegendClickIllumination = (e) => {
        const { dataKey } = e;
        setVisibleIlluminationLines(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };

    useEffect(() => {
        if (selectedTimeRange) {
            const today = new Date();
            setEndDate(new Date());
            let startDateCopy = new Date(today);

            switch (selectedTimeRange) {
                case translations.one_week:
                    startDateCopy.setDate(today.getDate() - 7);
                    break;
                case translations.two_weeks:
                    startDateCopy.setDate(today.getDate() - 14);
                    break;
                case translations.one_month:
                    startDateCopy.setDate(today.getDate() - 30);
                    break;
                case translations.three_months:
                    startDateCopy.setMonth(today.getMonth() - 3);
                    break;
                case translations.six_months:
                    startDateCopy.setMonth(today.getMonth() - 6);
                    break;
                case translations.one_year:
                    startDateCopy.setFullYear(today.getFullYear() - 1);
                    break;
                default:
                    break;
            }
            setStartDate(new Date(startDateCopy));
        }
    }, [selectedTimeRange, translations]);

    useEffect(() => {
        const fetchIrrigations = async () => {
            const token = localStorage.getItem('userToken');
            const response = await fetchIrrigationsForPot(pot.id, token);
            if (!response.error) {
                setIrrigations(response.data);
            }
        };
        fetchIrrigations();
    }, [pot.id]);

    const downsampleData = useMemo(() => (data) => {
        return data.map(val => ({
            time: val.time,
            value: val.value
        }));
    }, []);

    const calculateSMA = useMemo(() => (data, period) => {
        let smaData = [];
        for (let i = 0; i < period - 1; i++) {
            smaData.push({ time: data[i].time, value: data[i].value });
        }
        for (let i = period - 1; i < data.length; i++) {
            const currentValue = data[i].value;
            if (currentValue < 10 || currentValue > 90) {
                smaData.push({ time: data[i].time, value: currentValue });
            } else {
                let sum = 0;
                for (let j = i - period + 1; j <= i; j++) {
                    sum += data[j].value;
                }
                smaData.push({ time: data[i].time, value: sum / period });
            }
        }
        return smaData;
    }, []);

    const normalizeSoilMoistureMain = value => {
        if (value == null) return null;
        let normalized = ((3150 - value) / (3150 - 2150)) * 100;
        if (normalized < 0) normalized = 0;
        if (normalized > 100) normalized = 100;
        return normalized;
    };

    const normalizeSoilMoistureAux = value => {
        if (value == null) return null;
        let normalized = ((2550 - value) / (2550 - 2250)) * 100;
        if (normalized < 0) normalized = 0;
        if (normalized > 100) normalized = 100;
        return normalized;
    };

    const groundHumidityDualData = useMemo(() => {
        return rawPotData.map(d => ({
            time: new Date(d.timestamp).getTime(),
            realMain: d.ground_humidity,
            realAux: d.ground_humidity_2,
            procMain: normalizeSoilMoistureMain(d.ground_humidity),
            procAux: normalizeSoilMoistureAux(d.ground_humidity_2),
        })).filter(item => item.realMain != null && item.realAux != null);
    }, [rawPotData]);

    useEffect(() => {
        const processData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('userToken');
                const potResponse = await fetchMeasuresForPotByDate(pot.id, startDate, endDate, token);
                const hubResponse = await fetchMeasuresForHubByDate(pot.hub_id, startDate, endDate, token);
                const potData = potResponse.data || [];
                setRawPotData(potData);
                const hubData = hubResponse.data || [];

                potData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                hubData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

                const newChartData = {
                    temperature: potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: Number(data.temperature) })),
                    minerals: hubData.length > 0 ? hubData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.minerals })) : [],
                    soilMoisture: potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.ground_humidity })),
                    soilMoisture_2: potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.ground_humidity_2 })),
                    airHumidity: potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.air_humidity })),
                    illumination: potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.illumination })),
                    temperatureSMA: calculateSMA(potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: Number(data.temperature) })), 5),
                    mineralsSMA: hubData.length > 0 
                        ? calculateSMA(hubData.map(data => ({
                            time: new Date(data.timestamp).getTime(), 
                            value: data.minerals
                        })), 5)
                        : [],
                    soilMoistureSMA: calculateSMA(potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.ground_humidity })), 5), 
                    soilMoistureSMA_2: calculateSMA(potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.ground_humidity_2 })), 5), 
                    airHumiditySMA: calculateSMA(potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.air_humidity })), 5), 
                    illuminationSMA: calculateSMA(potData.map(data => ({ time: new Date(data.timestamp).getTime(), value: data.illumination })), 5), 
                };
                setChartData(newChartData);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        };
        processData();
    }, [startDate, endDate, selectedTimeRange, pot.id, pot.hub_id, calculateSMA]);

    const createGroundHumidityChartData = (data) => {
        return data.map(item => ({
            time: item.time,
            value: item.value,
            smaValue: chartData.soilMoistureSMA.find(sma => sma.time === item.time)?.value,
            value2: chartData.soilMoisture_2.find(sm => sm.time === item.time)?.value,
            smaValue2: chartData.soilMoistureSMA_2.find(sma => sma.time === item.time)?.value,
        }));
    };

    const createChartData = (data, smaData) => {
        return data.map(item => ({
            time: item.time,
            value: item.value,
            smaValue: smaData.find(sma => sma.time === item.time)?.value
        }));
    };

    const renderIrrigationAreasGround = (minX, maxX) => {
        return irrigations.map(irrigation => {
            let start = new Date(irrigation.startDate).getTime();
            let end = new Date(irrigation.endDate).getTime();
            if (end < minX || start > maxX) return null;
            if (start < minX) start = minX;
            if (end > maxX) end = maxX;
            return (
                <ReferenceArea
                    key={irrigation.id}
                    x1={start}
                    x2={end}
                    yAxisId="left"
                    stroke="blue"
                    fill="blue"
                    fillOpacity={0.18}
                />
            );
        });
    };

    if (isLoading) {
        return <div className='pot-loading'><img src={loadingGif} alt="Loading..." /></div>;
    }

    const getMinMax = data => {
        if (!data || data.length === 0) return [0, 0];
        const min = Math.min(...data.map(d => d.time));
        const max = Math.max(...data.map(d => d.time));
        return [min, max];
    };

    return (
        <div className="pot-page">
            <div className="pot-container">
                <div className="header">
                    <button onClick={() => navigate(-1)} className="back-button">&#8592;</button>
                    <h1>{pot.name}</h1>
                </div>
                <div className="date-picker-container">
                    <ReactDatePicker
                        selected={startDate}
                        onChange={date => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        dateFormat="dd/MM/yyyy"
                    />
                    <ReactDatePicker
                        selected={endDate}
                        onChange={date => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        dateFormat="dd/MM/yyyy"
                    />
                </div>
                <select className="irrigate-select2" value={selectedTimeRange} onChange={(e) => setSelectedTimeRange(e.target.value)}>
                    {timeRangeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
                
                <div className="graph-container">
                    <h2 className="chart-title">{translations.temperature}</h2>
                    {chartData.temperature.length > 0 ? (() => {
                        const [minX, maxX] = getMinMax(chartData.temperature);
                        return (
                        <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={createChartData(chartData.temperature, chartData.temperatureSMA)}>
                                <XAxis 
                                    dataKey="time"
                                    type="number"
                                    domain={[minX, maxX]}
                                    scale="time"
                                    tickFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')}
                                    ticks={
                                        chartData.temperature.length > 5
                                            ? chartData.temperature
                                                .map(d => d.time)
                                                .filter((_, i, arr) => i % Math.ceil(arr.length / 5) === 0)
                                            : chartData.temperature.map(d => d.time)
                                    }
                                />
                                <YAxis domain={[5.0, 40.0]} />
                                <Tooltip labelFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')} />
                                <Legend onClick={handleLegendClickTemperature} />
                                <ReferenceLine y={pot.max_temperature} stroke="green"/>
                                <ReferenceLine y={pot.min_temperature} stroke="green"/>
                                <ReferenceArea y1={pot.min_temperature} y2={pot.max_temperature} strokeOpacity={0} fill="green" fillOpacity={0.3} />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#8884d8"
                                    name={translations.real_time_value}
                                    hide={!visibleTemperatureLines.value}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="smaValue"
                                    stroke="#82ca9d"
                                    name={translations.SMA}
                                    hide={!visibleTemperatureLines.smaValue}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )})() : (
                        <p>{translations.no_data_temperature}</p>
                    )}
                </div>
                
                {chartData.minerals.length > 0 && (() => {
                    const [minX, maxX] = getMinMax(chartData.minerals);
                    return (
                    <div className="graph-container">
                        <h2 className="chart-title">{translations.minerals}</h2>
                        <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={createChartData(chartData.minerals, chartData.mineralsSMA)}>
                                <XAxis 
                                    dataKey="time"
                                    type="number"
                                    domain={[minX, maxX]}
                                    scale="time"
                                    tickFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')}
                                    ticks={
                                        chartData.minerals.length > 5
                                            ? chartData.minerals
                                                .map(d => d.time)
                                                .filter((_, i, arr) => i % Math.ceil(arr.length / 5) === 0)
                                            : chartData.minerals.map(d => d.time)
                                    }
                                />
                                <YAxis domain={[0, 4000]} />
                                <Tooltip labelFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')} />
                                <Legend onClick={handleLegendClickMinerals} />
                                <ReferenceLine y={pot.max_minerals} stroke="green"/>
                                <ReferenceLine y={pot.min_minerals} stroke="green"/>
                                <ReferenceArea y1={pot.min_minerals} y2={pot.max_minerals} strokeOpacity={0} fill="green" fillOpacity={0.3} />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#8884d8"
                                    name={translations.real_time_value}
                                    hide={!visibleMineralsLines.value}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="smaValue"
                                    stroke="#82ca9d"
                                    name={translations.SMA}
                                    hide={!visibleMineralsLines.smaValue}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )})}

                <div className="graph-container">
                    <h2 className="chart-title">{translations.ground_humidity}</h2>
                    {groundHumidityDualData.length > 0 ? (() => {
                        const [minX, maxX] = getMinMax(groundHumidityDualData);
                        return (
                        <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={groundHumidityDualData}>
                                <XAxis 
                                    dataKey="time"
                                    type="number"
                                    domain={[minX, maxX]}
                                    scale="time"
                                    tickFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')}
                                    ticks={
                                        groundHumidityDualData.length > 5
                                            ? groundHumidityDualData
                                                .map(d => d.time)
                                                .filter((_, i, arr) => i % Math.ceil(arr.length / 5) === 0)
                                            : groundHumidityDualData.map(d => d.time)
                                    }
                                />
                                <YAxis
                                    yAxisId="left"
                                    domain={[
                                        Math.min(
                                            ...groundHumidityDualData.map(d => d.realMain ?? 0),
                                            ...groundHumidityDualData.map(d => d.realAux ?? 0)
                                        ) - 50,
                                        Math.max(
                                            ...groundHumidityDualData.map(d => d.realMain ?? 0),
                                            ...groundHumidityDualData.map(d => d.realAux ?? 0)
                                        ) + 50
                                    ]}
                                    orientation="left"
                                    label={{ value: translations.sensor_value || 'Sensor', angle: -90, position: 'insideLeft' }}
                                    stroke="#0ea5e9"
                                    tick={{ fill: "#0ea5e9" }}
                                />
                                <YAxis
                                    yAxisId="right"
                                    domain={[0, 100]}
                                    orientation="right"
                                    reversed={true}
                                    label={{ value: translations.processed_value || 'Percent', angle: 90, position: 'insideRight' }}
                                    stroke="#facc15"
                                    tick={{ fill: "#facc15" }}
                                />
                                <Tooltip labelFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')} />
                                <Legend onClick={handleLegendClickSoilMoisture} />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="realMain"
                                    stroke="#0ea5e9"
                                    dot={false}
                                    name={(translations.real_time_value || "Value") + " MAIN"}
                                    hide={!visibleSoilMoistureLines.realMain}
                                />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="realAux"
                                    stroke="#6366f1"
                                    dot={false}
                                    name={(translations.real_time_value || "Value") + " AUX"}
                                    hide={!visibleSoilMoistureLines.realAux}
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="procMain"
                                    stroke="#111111"
                                    strokeDasharray="5 2"
                                    dot={false}
                                    name={(translations.processed_value || "PROCESSED") + " MAIN"}
                                    hide={!visibleSoilMoistureLines.procMain}
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="procAux"
                                    stroke="#facc15"
                                    strokeDasharray="5 2"
                                    dot={false}
                                    name={(translations.processed_value || "PROCESSED") + " AUX"}
                                    hide={!visibleSoilMoistureLines.procAux}
                                />
                                {renderIrrigationAreasGround(minX, maxX)}
                            </LineChart>
                        </ResponsiveContainer>
                    )})() : (
                        <p>{translations.no_data_ground_humidity}</p>
                    )}
                </div>

                <div className="graph-container">
                    <h2 className="chart-title">{translations.air_humidity}</h2>
                    {chartData.airHumidity.length > 0 ? (() => {
                        const [minX, maxX] = getMinMax(chartData.airHumidity);
                        return (
                        <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={createChartData(chartData.airHumidity, chartData.airHumiditySMA)}>
                                <XAxis 
                                    dataKey="time"
                                    type="number"
                                    domain={[minX, maxX]}
                                    scale="time"
                                    tickFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')}
                                    ticks={
                                        chartData.airHumidity.length > 5
                                            ? chartData.airHumidity
                                                .map(d => d.time)
                                                .filter((_, i, arr) => i % Math.ceil(arr.length / 5) === 0)
                                            : chartData.airHumidity.map(d => d.time)
                                    }
                                />
                                <YAxis domain={[35, 95]} />
                                <Tooltip labelFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')} />
                                <Legend onClick={handleLegendClickAirHumidity} />
                                <ReferenceLine y={pot.max_air_humidity} stroke="green"/>
                                <ReferenceLine y={pot.min_air_humidity} stroke="green"/>
                                <ReferenceArea y1={pot.min_air_humidity} y2={pot.max_air_humidity} strokeOpacity={0} fill="green" fillOpacity={0.3} />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#8884d8"
                                    name={translations.real_time_value}
                                    hide={!visibleAirHumidityLines.value}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="smaValue"
                                    stroke="#82ca9d"
                                    name={translations.SMA}
                                    hide={!visibleAirHumidityLines.smaValue}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )})() : (
                        <p>{translations.no_data_air_humidity}</p>
                    )}
                </div>
                <div className="graph-container">
                    <h2 className="chart-title">{translations.sunlight_level}</h2>
                    {chartData.illumination.length > 0 ? (() => {
                        const illuminationLogData = createChartData(
                            chartData.illumination.map(d => ({
                                ...d,
                                value: d.value < 1 ? 1 : d.value
                            })),
                            chartData.illuminationSMA.map(d => ({
                                ...d,
                                value: d.value < 1 ? 1 : d.value
                            }))
                        );
                        const [minX, maxX] = getMinMax(illuminationLogData);
                        return (
                            <ResponsiveContainer width="100%" height={400}>
                                <LineChart data={illuminationLogData}>
                                    <XAxis 
                                        dataKey="time"
                                        type="number"
                                        domain={[minX, maxX]}
                                        scale="time"
                                        tickFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')}
                                        ticks={
                                            illuminationLogData.length > 5
                                                ? illuminationLogData
                                                    .map(d => d.time)
                                                    .filter((_, i, arr) => i % Math.ceil(arr.length / 5) === 0)
                                                : illuminationLogData.map(d => d.time)
                                        }
                                    />
                                    <YAxis domain={[1, 100]} scale="log" />
                                    <Tooltip labelFormatter={time => moment(time).format('DD/MM/YYYY HH:mm')} />
                                    <Legend onClick={handleLegendClickIllumination} />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#8884d8"
                                        name={translations.real_time_value}
                                        hide={!visibleIlluminationLines.value}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="smaValue"
                                        stroke="#82ca9d"
                                        name={translations.SMA}
                                        hide={!visibleIlluminationLines.smaValue}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        );
                    })() : (
                        <p>{translations.no_data_sunlight_level}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PotDetailsADMIN;
